// AM-5716 follow-up — pure helpers for the PP member route id
// (`pp:<companyId>:<mpi>` or, as of AM-5716, `pp:<companyId>:<mpi>:<mkey>`)
// extracted out of MemberProfilePage so the parsing + the not-found-guard
// predicate are directly unit-testable. This is exactly the class of
// edge case (legacy 3-segment ids, an empty-but-present mkey segment,
// non-PP ids) that produced the first, wrong version of this fix — cover it
// with real tests rather than trusting inline component logic again.

export interface ParsedPpMemberId {
  companyId: string;
  mpi: string;
  /** Undefined for both "segment absent" (legacy 3-segment id) and "segment
   *  present but empty" (`pp:company:mpi:`) — callers never need to
   *  distinguish those two, both mean "no mkey to resolve with". */
  mkey: string | undefined;
}

/**
 * Parse a PP member route id into its opaque GUID parts. Returns `undefined`
 * for anything that isn't a `pp:`-prefixed id (a Pathways Mongo ObjectId, an
 * `astrana:<astranaId>` connect-only id, or anything else) — callers must be
 * able to tell "not a PP route" apart from "a PP route with empty fields"
 * rather than guessing a shape for an id this was never built to parse.
 */
export function parsePpMemberId(
  id: string | undefined | null,
): ParsedPpMemberId | undefined {
  if (!id?.startsWith("pp:")) return undefined;
  const idParts = id.split(":");
  return {
    companyId: idParts[1] ?? "",
    mpi: idParts[2] ?? "",
    mkey: idParts[3] || undefined,
  };
}

/**
 * True when a PP member can never resolve to a renderable patient and this
 * host should show its own "Member not found" recovery state instead of ever
 * handing `@astrana/ui` an unresolvable patient object. `companyId`/`mpi`
 * missing is always unresolvable; a missing `mkey` is ONLY unresolvable on a
 * hard refresh (`isRefresh`) — on the normal (location.state-present) path
 * the real `membId` from the search row lets `(memberId && mpi)` resolve the
 * header even without mkey, so a null-mkey member is no worse off there than
 * it always has been.
 */
export function isPpMemberUnresolvable(params: {
  companyId: string;
  mpi: string;
  mkey: string | undefined;
  isRefresh: boolean;
}): boolean {
  return !params.companyId || !params.mpi || (params.isRefresh && !params.mkey);
}

/**
 * True when the host should fall back to a `connectPatient`-by-mkey lookup
 * (CONNECT_PATIENT_IDS) to resolve the real business membId (RmiMembId) for
 * Create-Auth / the Duplicate-auth hint — i.e. whenever we have enough to
 * run that lookup (mkey + companyId) AND we don't already have a real membId
 * from a search row. Deliberately NOT scoped to "hard refresh only": a
 * normal (location.state-present) PP row whose search result had a null
 * `membId` needs the exact same fallback, or Create-Auth fails permanently
 * with no recovery for that member.
 */
export function shouldResolvePpMemberIdFallback(params: {
  mkey: string | undefined;
  companyId: string;
  membId: string | null | undefined;
}): boolean {
  return !!params.mkey && !!params.companyId && !params.membId;
}
